<template>
  <v-flex
    xs12
  >
    <v-layout
      row
      wrap
    >
      <v-flex
        xs12
        md4
        offset-md1
      >
        <v-flex :class="disabled ? 'text--disabled' : ''">
          <h3 class="title font-weight-regular pt-3">
            {{ $t('Tax Groups') }}
          </h3>
          <span class="caption">
            {{ $t('Customize tax groups, their zone codes, taxonomy and attached tax rates.') }}
          </span>
        </v-flex>
        <v-flex>
          <v-btn
            :disabled="!taxRates || !taxRates.length || disabled"
            class="mx-0"
            @click="selectTaxGroup('__')"
          >
            {{ $t('Add tax group') }}
          </v-btn>
        </v-flex>
      </v-flex>
      <v-flex
        xs12
        md6
      >
        <v-card
          flat
          class="pa-3"
        >
          <LoadingStateMicro
            v-if="!taxGroups"
            :loading="true"
          />
          <template v-else>
            <v-list-tile v-if="!taxGroups || !taxGroups.length || !taxRates || !taxRates.length">
              <span>{{ $t('No tax groups to display') }}
                <template
                  v-if="!taxRates.length"
                >
                  or edit <br >
                  <span class="body-1">{{ $t('Add tax rate first') }}</span>
                </template>
              </span>
            </v-list-tile>

            <div v-else>
              <v-list
                v-for="(group, index) in taxGroups"
                :key="group.id"
                two-line
                subheader
              >
                <v-list-tile
                  :disabled="disabled"
                  @click="selectTaxGroup(group.id)"
                >
                  <v-list-tile-content>
                    {{ group.name }}
                  </v-list-tile-content>
                </v-list-tile>
                <v-flex
                  v-for="rate in group.rates"
                  :key="rate.id"
                  :class="disabled ? 'text--disabled' : ''"
                  xs12
                  class="d-flex my-0 py-0"
                >
                  <v-flex>
                    {{ rate.name }}
                  </v-flex >
                  <v-spacer />
                  <v-flex xs4>
                    {{ $t('Rate:') }} {{ rate.rate }}
                  </v-flex>
                </v-flex>
                <v-divider
                  v-if="index < taxGroups.length-1"
                  class="mt-3"
                />
              </v-list>
            </div>
          </template>
        </v-card>
      </v-flex>
    </v-layout>
    <template
      v-if="dialog"
    >
      <v-dialog
        v-model="dialog"
        persistent
        max-width="600px"
      >
        <v-card>
          <SettingsTaxGroupForm
            v-if="selectedTaxGroup"
            :tax-group="selectedTaxGroup"
            :taxonomy-tree="taxonomyTree"
            :taxonomy="taxonomy"
            :tax-rates="taxRates"
            :zones="zones"
            :customer-groups="customerGroups"
          />
          <template v-else>
            <v-card-title />
            <loading-state-micro
              :loading="true"
            />
          </template>
          <v-card-actions>
            <v-btn
              v-if="selectedTaxGroup && selectedTaxGroup.id"
              color="darken-1"
              flat
              small
              @click="removeTaxGroup"
            >
              {{ $t('Delete') }}
            </v-btn>

            <v-spacer />
            <v-btn
              color="darken-1"
              flat
              small
              @click="dialog=false"
            >
              {{ $t('Close') }}
            </v-btn>
            <v-btn
              color="primary darken-1"
              flat
              small
              @click="saveTaxGroup"
            >
              {{ $t('Save') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </v-flex>
</template>

<script>
import TaxMixin from './TaxesMixin'
import SettingsTaxGroupForm from '@/modules/Settings/components/Taxes/SettingsTaxGroupForm'

export default {
  name: 'TaxGroup',

  components: {
    SettingsTaxGroupForm
  },

  mixins: [ TaxMixin ],

  props: {
    taxGroups: {
      type: Array,
      default: () => {}
    },
    taxRates: {
      type: Array,
      default: () => {}
    },
    zones: {
      type: Array,
      default: () => []
    },
    taxonomyTree: {
      type: Array,
      default: () => []
    },
    taxonomy: {
      type: Array,
      default: () => []
    },
    customerGroups: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      selectedTaxGroup: null,
      schema: {
        'zone_code': [],
        'taxonomy': '',
        'shipping_taxable': true,
        'customer_group': null,
        'name': '',
        'rates': [],
        'tax_rate_ids': []
      },
      dialog: false
    }
  },

  methods: {
    async selectTaxGroup (taxGroupId) {
      this.selectedTaxGroup = null
      let copySchema = { ...this.schema }
      this.dialog = true

      let taxGroup = null
      if (taxGroupId === '__') {
        taxGroup = await this.$http.make('taxGroups', copySchema)
      } else {
        taxGroup = await this.$http.make('taxGroups').find(taxGroupId)
        copySchema.id = taxGroupId
        this.rebindValues(taxGroup, copySchema, Object.keys(this.schema))
        taxGroup = await this.$http.make('taxGroups', copySchema)
      }

      this.selectedTaxGroup = taxGroup
    },

    async saveTaxGroup () {
      this.cleanFields(this.selectedTaxGroup)
      let copySchema = { ...this.schema }
      await this.rebindValues(this.selectedTaxGroup, copySchema, Object.keys(this.schema))
      if (this.selectedTaxGroup !== '__') {
        copySchema.id = this.selectedTaxGroup.id
      }

      const taxGroup = await this.$http.make('taxGroups', { ...copySchema })

      taxGroup.tax_rate_ids = taxGroup.rates.map(rate => rate.id)

      taxGroup
        .save()
        .then(() => {
          this.closeDialogMixin(this.$t('Tax group saved'))
        })
        .catch(e => {
          this.$vuetify.goTo(0)
          this.selectedTaxGroup.errors = [...taxGroup.errors]
          taxGroup.errors.map(error => {
            this.$toast.error(this.$t(error.message.join('\n')))
          })
        })
    },

    async removeTaxGroup () {
      await this.selectedTaxGroup
        .delete()
        .then(() => {
          this.closeDialogMixin(this.$t('Tax group removed'))
        })
    }
  }
}
</script>

<style lang="stylus" scoped>
  .clickable {
    cursor: pointer;
  }
</style>
